import { useModules } from '@contexts/modules-provider';
import { HeaderOnlyLayout } from '@layouts/header-only-layout/HeaderOnlyLayout';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { AppLoader } from '../../app-loader/AppLoader';

/**
 * ModuleGuard - Guards module-scoped routes (/train, /instruct)
 *
 * Responsibilities:
 * - Verify user has access to the required module (Train or Instruct)
 * - Auto-select the module based on URL path
 *
 * Note: Manage routes use OrganizationRouteGuard instead.
 */
const ModuleGuard = React.memo((): React.ReactElement => {
  const location = useLocation();
  const { modules, selectedModule, selectModule, isModulesLoading } =
    useModules();
  const [isAutoSelecting, setIsAutoSelecting] = React.useState(false);
  const attemptedModuleRef = React.useRef<string | null>(null);

  const requiredModule = React.useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/train')) return 'Train';
    if (path.startsWith('/instruct')) return 'Instruct';
    return null;
  }, [location.pathname]);

  const hasModuleAccess = React.useMemo(() => {
    if (!requiredModule) return false;
    return modules?.some(m => m.name === requiredModule) ?? false;
  }, [modules, requiredModule]);

  // Reset attempted module ref when the required module changes (new route)
  React.useEffect(() => {
    if (requiredModule !== attemptedModuleRef.current) {
      attemptedModuleRef.current = null;
    }
  }, [requiredModule]);

  React.useEffect(() => {
    if (isAutoSelecting || attemptedModuleRef.current === requiredModule)
      return;
    if (!requiredModule || !hasModuleAccess) return;
    if (selectedModule?.name === requiredModule) return;

    attemptedModuleRef.current = requiredModule;
    setIsAutoSelecting(true);
    selectModule(requiredModule)
      .then(() => {
        setIsAutoSelecting(false);
      })
      .catch(() => {
        setIsAutoSelecting(false);
      });
  }, [
    requiredModule,
    hasModuleAccess,
    selectedModule,
    isAutoSelecting,
    selectModule,
  ]);

  if (isModulesLoading) {
    return <AppLoader />;
  }

  if (!hasModuleAccess) {
    return (
      <HeaderOnlyLayout>
        <InlineFeedback type="not-found" />
      </HeaderOnlyLayout>
    );
  }

  if (isAutoSelecting || selectedModule?.name !== requiredModule) {
    return <AppLoader />;
  }

  return <Outlet />;
});

ModuleGuard.displayName = 'ModuleGuard';

export { ModuleGuard };
